/**
 * Insights — "what f8 learned", the write-back review queue (PPL-265).
 * Route: /insights
 *
 * Implements pplkpr-insights-web.html from the design project: the app's
 * review screen rebuilt as a desktop triage surface — j/k moves, a saves,
 * x dismisses, focus jumps to the next pending item, the extracted value is
 * editable before saving, and the ledger of what f8 already knows sits
 * beside the queue.
 *
 * Same rules as the app (they come from the backend, not the surface):
 * strong matches save themselves and collapse to a receipt with undo;
 * everything else waits to be accepted; every line carries provenance;
 * accepting collapses IN PLACE to a receipt with undo; undo of an accept is
 * terminal ("not saved, f8 won't suggest this again") unless it is answered
 * with "not <name>?", which also tombstones the alias; dismiss is reversible
 * only inside the deferred-send window; undecided items expire at 30 days.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ChevronRight, Edit2, Plus, UserPlus } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import SEO from '../components/SEO';
import MonoAvatar from '../components/MonoAvatar';
import { useWriteback, useWritebackItems } from '../contexts/WritebackContext';
import {
  IDENTITY_TYPES,
  candidateSummary,
  fetchSavedLedger,
  matchLabel,
  type SavedLedger,
  type SuggestionItem,
} from '../services/writebackService';

/* ── palette: the design's WI tokens, expressed via site vars where they exist ── */
const T = {
  panel: 'var(--glass-bg, rgba(255,255,255,0.032))',
  border: 'var(--glass-border, rgba(255,255,255,0.08))',
  muted: 'var(--text-muted, rgba(232,220,250,0.7))',
  dim: 'var(--text-dim, rgba(220,200,240,0.5))',
  // Feather's `color` prop lands in the SVG `stroke` ATTRIBUTE, where var()
  // is invalid — icon colors must be concrete or the glyph renders invisible.
  iconDim: 'rgba(220,200,240,0.5)',
  magenta: '#f0abfc',
  magenta2: '#d946ef',
  violet: '#a78bfa',
  green: '#34d399',
  amber: '#fdba74',
  sky: '#38bdf8',
};

const KIND_TONE: Record<string, string> = {
  fact: T.violet, event: T.sky, date: T.magenta, commitment: T.amber, note: T.green, identity: T.magenta,
};

const kbd: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 19, height: 19, padding: '0 5px', borderRadius: 5,
  background: 'rgba(255,255,255,0.07)', border: `1px solid ${T.border}`, borderBottomWidth: 2,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 10.5, fontWeight: 700,
  color: 'rgba(240,232,250,0.8)',
};

function Key({ children }: { children: React.ReactNode }) {
  return <kbd style={kbd}>{children}</kbd>;
}

function TypePill({ kind }: { kind: string }) {
  const c = KIND_TONE[kind] || T.violet;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 999,
      background: `${c}1c`, border: `1px solid ${c}3d`,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: c,
    }}>{kind === 'identity' ? 'who' : kind}</span>
  );
}

function Btn({ children, kind = 'quiet', onClick, disabled, style, title }: {
  children: React.ReactNode;
  kind?: 'primary' | 'quiet' | 'ghost';
  onClick?: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  title?: string;
}) {
  const kinds: Record<string, React.CSSProperties> = {
    primary: { background: 'var(--gradient-primary, linear-gradient(135deg,#d946ef,#8b5cf6))', border: '1px solid transparent', color: '#fff' },
    quiet: { background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: 'rgba(240,232,250,0.88)' },
    ghost: { background: 'transparent', border: '1px solid transparent', color: T.dim },
  };
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{
      appearance: 'none', cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 9,
      fontSize: 12.5, fontWeight: 600, opacity: disabled ? 0.5 : 1, ...kinds[kind], ...style,
    }}>{children}</button>
  );
}

/* ── one settled receipt row ─────────────────────────────────────────── */
function ReceiptRow({ item, state, personName, firstName, onUndo, onWrongPerson, onUndoDismiss }: {
  item: SuggestionItem;
  state: {
    status: string;
    undoable: boolean;
    pendingCall?: boolean;
    personName?: string | null;
    createdPerson?: boolean;
    autoSaved?: boolean;
  };
  personName: string | null;
  firstName: string | null;
  onUndo: () => void;
  onWrongPerson: () => void;
  onUndoDismiss: () => void;
}) {
  if (state.status === 'conflicted') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderRadius: 12,
        background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.24)',
      }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#fde8b0' }}>
          {/* A conflict can be partial; the one always-true statement is that
              whatever the user changed by hand was left alone. */}
          this changed since — your version was kept
        </span>
      </div>
    );
  }

  const good = state.status === 'accepted';
  const undone = state.status === 'undone';
  // Receipts name the person by FIRST name — Carl's copy ("saved to John"),
  // and app parity. `firstName` is null for a person created by this very
  // accept (not yet in the people map), so fall back to the first token of
  // the display name rather than to nothing.
  const fullName = state.personName || personName;
  const who = firstName || (fullName || '').split(' ')[0] || null;
  // `new_person` is the PROPOSAL; `createdPerson` is what actually happened.
  // The two diverge whenever the item was answered by picking an existing
  // candidate — a previously un-linked name comes back as `new_person` with
  // that same person still listed — and "added Sarah to my ppl" about someone
  // already in my ppl is a plainly false receipt. While the call is still in
  // flight there is no answer yet, so fall back to the one case where creation
  // is certain: a new_person item with nothing else it could have targeted.
  const created = state.pendingCall ?
    item.type === 'new_person' && item.candidates.length === 0 :
    state.createdPerson === true;
  // The score belongs on receipts f8 wrote ITSELF. On an item the user
  // accepted there is nothing to be confident about — they decided.
  const score = state.autoSaved && item.confidencePct !== null ? ` · ${item.confidencePct}%` : '';
  // "not john?" is the WRONG-PERSON answer: it reverts and tombstones the
  // alias. Offered only where the two answers actually differ — on any
  // IDENTITY type the plain undo already tombstones, so a second verb there
  // would do the same thing under a different name — and an item with no
  // person has nobody to be wrong about.
  const wrongPersonName = who;
  const offerWrongPerson =
    !!state.autoSaved && !!item.personId
    && !IDENTITY_TYPES.includes(item.type) && !!wrongPersonName;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderRadius: 12,
      background: good ? 'rgba(52,211,153,0.08)' : 'rgba(255,255,255,0.025)',
      border: `1px solid ${good ? 'rgba(52,211,153,0.26)' : T.border}`,
    }}>
      <span style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        background: good ? 'rgba(52,211,153,0.2)' : 'rgba(255,255,255,0.07)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {good ? <Check size={12} color={T.green} strokeWidth={3} /> : <X size={11} color={T.iconDim} />}
      </span>
      <span style={{
        flex: 1, minWidth: 0, fontSize: 13, color: good ? 'rgba(240,232,250,0.85)' : T.dim,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {good
          ? created
            // Creating a contact and filing a note are different outcomes;
            // one receipt for both leaves the user unsure which happened.
            ? <>added <span style={{ color: T.dim }}>{who || item.to || item.text}</span> to my ppl</>
            : <>saved to {who || 'their timeline'}{score} — <span style={{ color: T.dim }}>{item.to || item.text}</span></>
          : undone
            ? <>not saved · f8 won&apos;t suggest this again</>
            : <>dismissed{state.undoable ? '' : " · f8 won't suggest this again"}</>}
      </span>
      {good && !state.pendingCall && offerWrongPerson ? (
        <button onClick={onWrongPerson} title="wrong person — f8 won't link this name to them again" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600, color: T.muted, whiteSpace: 'nowrap' }}>not {wrongPersonName}?</button>
      ) : null}
      {good && !state.pendingCall ? (
        <button onClick={onUndo} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: T.magenta }}>undo</button>
      ) : null}
      {state.status === 'dismissed' && state.undoable ? (
        <button onClick={onUndoDismiss} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: T.magenta }}>undo</button>
      ) : null}
    </div>
  );
}

/* ── one reviewable item ─────────────────────────────────────────────── */
function QueueItem({ item, focused, people, onFocus, onAccept, onEdit, onDismiss, onChoose }: {
  item: SuggestionItem;
  focused: boolean;
  people: Record<string, { name: string }>;
  onFocus: () => void;
  onAccept: (chosenPersonId?: string) => void;
  onEdit: (edits: Record<string, string>, chosenPersonId?: string) => void;
  onDismiss: () => void;
  onChoose: (personId: string) => void;
}) {
  const c = KIND_TONE[item.kind] || T.violet;
  const [editing, setEditing] = useState(false);
  // Seeded from the RAW payload field, never the rendered sentence — the
  // gate (`item.editable`) guarantees the two coincide when editing shows.
  const [val, setVal] = useState(item.editValue ?? item.to ?? '');
  // Unassigned CONTENT items ("their father died", no name to alias-match)
  // need the user to say who they're about — the server refuses an accept
  // with no target (invalid-argument) for content types, so the picker is
  // REQUIRED, not decorative. "someone new" is deliberately absent here: the
  // server would name the new person from mentionText ("their father"),
  // which is not a name. Two shapes are exempt: identity choosers (their
  // candidate buttons ARE the picker) and `new_person` items — the ONE type
  // the server auto-creates for on a bare accept (named from the extracted
  // firstName), so gating it would block a working accept and the only
  // enabled path would mis-link the new name to an existing contact, which
  // rememberAlias would then make permanent.
  const [assignTo, setAssignTo] = useState('');
  const [rosterOpen, setRosterOpen] = useState(false);
  // calendar_attendee only (PPL-340): the "someone new" path needs a NAME —
  // the server refuses a create-new accept without edits.firstName, because a
  // person must never be named by their email address. namingNew reveals the
  // input; accept then goes out as edit({firstName}, 'new').
  const [namingNew, setNamingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const roster = Object.entries(people)
    .map(([id, p]) => ({ id, name: p.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const needsAssign = !item.personId && item.type !== 'new_person' &&
    !(item.kind === 'identity' && item.candidates.length > 0);
  // A `new_person` accept doesn't file a note against someone you already
  // know — it CREATES the contact. "save" undersells that to the point of
  // being surprising: the button that reads like every other save is the one
  // that adds a person to my ppl, so it says so.
  const isNewPerson = item.type === 'new_person';

  useEffect(() => {
    if (focused && ref.current) {
      ref.current.focus({ preventScroll: true });
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [focused]);

  const commitEdit = () => {
    // Same gate as the save button: no target yet means Enter is a no-op —
    // stay editing (value kept) until "who is this about?" is answered.
    if (needsAssign && !assignTo) return;
    setEditing(false);
    const next = val.trim();
    if (!item.editable || !next || next === (item.editValue || '').trim()) {
      return onAccept(needsAssign ? assignTo : undefined);
    }
    return onEdit({ [item.editKey as string]: next }, needsAssign ? assignTo : undefined);
  };

  const isChooser = item.kind === 'identity' && item.candidates.length > 0;
  // PPL-454 — show how sure f8 is about WHO. Rendered only when a score
  // actually travelled with the doc: a bare name here would just repeat the
  // group header above.
  const match = item.personId && item.confidencePct !== null
    ? matchLabel(people[item.personId]?.name || item.personName, item.confidenceBand, item.confidencePct)
    : null;
  // "could be john or jon" — the shape of the ambiguity, no percentage: the
  // question is which one, not how sure.
  const couldBe = candidateSummary(
    item.candidates.map((cand) => people[cand.personId]?.name || cand.displayName),
  );

  return (
    <div
      ref={ref}
      tabIndex={0}
      onFocus={onFocus}
      onMouseEnter={onFocus}
      style={{
        outline: 'none', position: 'relative', borderRadius: 14, padding: '13px 16px 12px',
        background: focused ? 'rgba(217,70,239,0.07)' : T.panel,
        border: `1px solid ${focused ? 'rgba(217,70,239,0.38)' : T.border}`,
        boxShadow: focused ? '0 8px 30px rgba(0,0,0,0.35)' : 'none',
        transition: 'background .12s, border-color .12s',
      }}
    >
      {focused && (
        <span style={{ position: 'absolute', left: -1, top: 14, bottom: 14, width: 2, borderRadius: 2, background: 'linear-gradient(#f0abfc,#a855f7)' }} />
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, marginTop: 8, flexShrink: 0, boxShadow: `0 0 10px ${c}80` }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
            <TypePill kind={item.kind} />
            <span style={{ fontSize: 11.5, color: T.dim }}>{item.field}</span>
            {match ? (
              <span style={{
                display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 999,
                background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                fontSize: 10.5, fontWeight: 600, color: T.muted, fontVariantNumeric: 'tabular-nums',
              }}>{match}</span>
            ) : null}
          </div>

          <div style={{ fontSize: 14.5, fontWeight: 600, color: '#fff', letterSpacing: '-0.01em', lineHeight: 1.45 }}>
            {isChooser ? <>you mentioned {item.mentionText || 'someone'} — which one?</> : item.text}
          </div>

          {isChooser ? (
            <>
              {couldBe ? (
                <div style={{ fontSize: 12.5, color: T.muted, marginTop: 6 }}>{couldBe}</div>
              ) : null}
              {item.contextHint ? (
                <div style={{
                  marginTop: 9, padding: '8px 12px', borderRadius: 10, background: 'rgba(0,0,0,0.25)',
                  borderLeft: '2px solid rgba(217,70,239,0.3)', fontSize: 12.5, color: T.muted, fontStyle: 'italic', lineHeight: 1.5,
                }}>“{item.contextHint}”</div>
              ) : null}
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 10 }}>
                {item.candidates.map((cand) => (
                  <button key={cand.personId} onClick={() => onChoose(cand.personId)} style={{
                    appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 7px',
                    borderRadius: 999, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                    color: '#fff', fontSize: 12.5, fontWeight: 600,
                  }}>
                    <MonoAvatar name={people[cand.personId]?.name || cand.displayName || '?'} size={20} />
                    {people[cand.personId]?.name || cand.displayName || 'someone'}
                    {typeof cand.confidence === 'number' && Number.isFinite(cand.confidence) ? (
                      <span style={{ fontSize: 11, fontWeight: 600, color: T.dim, fontVariantNumeric: 'tabular-nums' }}>
                        {Math.max(0, Math.min(100, Math.round(cand.confidence * 100)))}%
                      </span>
                    ) : null}
                  </button>
                ))}
                <button onClick={() => onChoose('new')} style={{
                  appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px',
                  borderRadius: 999, background: 'transparent', border: `1px dashed rgba(255,255,255,0.16)`,
                  color: T.muted, fontSize: 12.5, fontWeight: 500,
                }}><Plus size={12} /> someone new</button>
                {!rosterOpen ? (
                  <button onClick={() => setRosterOpen(true)} style={{
                    appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    padding: '6px 12px', borderRadius: 999, background: 'transparent',
                    border: `1px dashed rgba(255,255,255,0.16)`, color: T.muted, fontSize: 12.5,
                  }}>someone else…</button>
                ) : (
                  <select
                    autoFocus
                    value=""
                    onChange={(e) => { if (e.target.value) onChoose(e.target.value); }}
                    style={{ fontFamily: 'inherit', fontSize: 12.5, color: '#fff', background: 'rgba(0,0,0,0.4)', border: `1px solid ${T.border}`, borderRadius: 999, padding: '6px 10px', cursor: 'pointer' }}
                  >
                    <option value="">pick from my ppl…</option>
                    {roster.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 9 }}>
              {item.from ? (
                <>
                  <span style={{ fontSize: 12, color: T.dim, textDecoration: 'line-through' }}>{item.from}</span>
                  <ChevronRight size={11} color={T.iconDim} />
                </>
              ) : null}
              {editing ? (
                <input
                  autoFocus
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  onBlur={() => setEditing(false)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') commitEdit();
                    if (e.key === 'Escape') { setVal(item.editValue ?? item.to ?? ''); setEditing(false); }
                  }}
                  style={{
                    fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, color: '#fff',
                    background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.magenta2}`,
                    borderRadius: 8, padding: '5px 10px', minWidth: 220, outline: 'none',
                  }}
                />
              ) : item.to ? (
                item.editable ? (
                  <button onClick={() => setEditing(true)} title="edit before saving" style={{
                    appearance: 'none', cursor: 'text', fontFamily: 'inherit',
                    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 10px',
                    borderRadius: 8, background: `${c}18`, border: `1px solid ${c}3a`,
                    color: '#fff', fontSize: 12.5, fontWeight: 600, textAlign: 'left',
                  }}>{val}<Edit2 size={11} color={T.iconDim} /></button>
                ) : (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', padding: '5px 10px',
                    borderRadius: 8, background: `${c}18`, border: `1px solid ${c}3a`,
                    color: '#fff', fontSize: 12.5, fontWeight: 600,
                  }}>{item.to}</span>
                )
              ) : null}
            </div>
          )}

          <div style={{ fontSize: 11.5, color: T.dim, marginTop: 9, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'radial-gradient(circle at 32% 30%, #f0abfc, #d946ef 55%, #7c3aed)' }} />
            {item.src} · waiting for you
          </div>

          {needsAssign ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, color: T.muted }}>who is this about?</span>
              <select
                value={assignTo}
                onChange={(e) => { setAssignTo(e.target.value); setNamingNew(false); }}
                style={{ fontFamily: 'inherit', fontSize: 12.5, color: assignTo ? '#fff' : T.dim, background: 'rgba(0,0,0,0.35)', border: `1px solid ${assignTo ? 'rgba(217,70,239,0.4)' : T.border}`, borderRadius: 9, padding: '6px 10px', cursor: 'pointer', maxWidth: 220 }}
              >
                <option value="">pick from my ppl…</option>
                {roster.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {item.type === 'calendar_attendee' && !namingNew ? (
                <button onClick={() => { setNamingNew(true); setAssignTo(''); setNewName(item.mentionText?.split('@')[0] || ''); }} style={{
                  appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px',
                  borderRadius: 999, background: 'transparent', border: `1px dashed rgba(255,255,255,0.16)`,
                  color: T.muted, fontSize: 12.5, fontWeight: 500,
                }}><Plus size={12} /> someone new</button>
              ) : null}
              {namingNew ? (
                <>
                  <input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter' && newName.trim()) onEdit({ firstName: newName.trim() }, 'new');
                      if (e.key === 'Escape') setNamingNew(false);
                    }}
                    placeholder="their name, as you'd say it"
                    style={{
                      fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, color: '#fff',
                      background: 'rgba(0,0,0,0.35)', border: `1px solid ${newName.trim() ? 'rgba(217,70,239,0.4)' : T.border}`,
                      borderRadius: 9, padding: '6px 10px', minWidth: 200, outline: 'none',
                    }}
                  />
                  <button onClick={() => setNamingNew(false)} title="back to the picker" style={{
                    appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    padding: '6px 10px', borderRadius: 999, background: 'transparent',
                    border: '1px solid transparent', color: T.dim, fontSize: 12.5,
                  }}>cancel</button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, opacity: focused ? 1 : 0.55, transition: 'opacity .12s' }}>
          <Btn kind="quiet" onClick={onDismiss} style={{ padding: '7px 11px' }}><X size={12} />no</Btn>
          {/* An identity question is answered by picking a person; a bare
              save has no target, so the chooser cards hide it. Unassigned
              CONTENT items need the picker filled first — the server refuses
              a target-less accept for them (new_person is the exception: a
              bare accept auto-creates, so its save is never gated). */}
          {!isChooser && (
            <Btn
              kind="primary"
              disabled={needsAssign && !assignTo && !(namingNew && newName.trim())}
              onClick={
                editing ? commitEdit :
                namingNew && newName.trim() ?
                  () => onEdit({ firstName: newName.trim() }, 'new') :
                  () => onAccept(needsAssign ? assignTo : undefined)
              }
              title={needsAssign && !assignTo && !namingNew ? 'pick who this is about first' : undefined}
            >
              {isNewPerson || namingNew ? (
                <><UserPlus size={12} />add to my ppl</>
              ) : (
                <><Check size={12} strokeWidth={3} />save</>
              )}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── the page ────────────────────────────────────────────────────────── */
export default function Insights() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();
  const {
    ready, error, resolved, pendingTotal, people,
    resolve, undo, dismiss, undoDismiss, clearResolved,
  } = useWriteback();
  const items = useWritebackItems();

  const [focus, setFocus] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [toast, setToast] = useState<string | null>(null);
  const [ledger, setLedger] = useState<SavedLedger | null>(null);
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1160);

  useEffect(() => {
    if (!isAuthenticated && ready) redirectToLogin();
  }, [isAuthenticated, ready, redirectToLogin]);

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1160);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The receipt overlay is per-visit: drop only the ids THIS page showed on
  // the way out, so the next visit starts from the server's truth.
  const shownRef = useRef<string[]>([]);
  shownRef.current = items.map((i) => i.id);
  useEffect(() => () => clearResolved(shownRef.current), [clearResolved]);

  // Sidebar ledger — real counts of accepted/edited items, or nothing.
  useEffect(() => {
    if (!user?.uid) return;
    let alive = true;
    fetchSavedLedger(user.uid).then((l) => { if (alive) setLedger(l); }).catch(() => {});
    return () => { alive = false; };
  }, [user?.uid, pendingTotal]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  /* ── grouping + filtering ── */
  const groups = useMemo(() => {
    const byPerson = new Map<string, SuggestionItem[]>();
    const unmatched: SuggestionItem[] = [];
    items.forEach((i) => {
      if (!i.personId) { unmatched.push(i); return; }
      if (!byPerson.has(i.personId)) byPerson.set(i.personId, []);
      (byPerson.get(i.personId) as SuggestionItem[]).push(i);
    });
    const out = [...byPerson.entries()].map(([pid, list]) => ({
      key: pid,
      personId: pid,
      name: people[pid]?.name || 'someone',
      list,
      pendingCount: list.filter((i) => !resolved[i.id]).length,
    }));
    out.sort((a, b) => b.pendingCount - a.pendingCount || a.name.localeCompare(b.name));
    if (unmatched.length) {
      out.push({
        key: '__unmatched__', personId: '__unmatched__', name: 'not matched yet',
        list: unmatched, pendingCount: unmatched.filter((i) => !resolved[i.id]).length,
      });
    }
    return out;
  }, [items, people, resolved]);

  const visibleGroups = filter === 'all' ? groups : groups.filter((g) => g.key === filter);
  const visibleItems = visibleGroups.flatMap((g) => g.list);
  const pendingVisible = visibleItems.filter((i) => !resolved[i.id]);
  const sessionTotal = items.length;
  const sessionDone = items.filter((i) => resolved[i.id]).length;
  const caughtUp = ready && pendingTotal === 0;

  /* ── actions ── */
  const advanceFrom = useCallback((id: string) => {
    const idx = visibleItems.findIndex((i) => i.id === id);
    const next =
      visibleItems.slice(idx + 1).find((i) => !resolved[i.id] && i.id !== id) ||
      visibleItems.find((i) => !resolved[i.id] && i.id !== id);
    setFocus(next ? next.id : null);
  }, [visibleItems, resolved]);

  const doAccept = useCallback(async (item: SuggestionItem, edits?: Record<string, string>, chosenPersonId?: string) => {
    advanceFrom(item.id);
    const result = await resolve(item.id, edits ? 'edit' : 'accept', { edits, chosenPersonId });
    setToast(result ? 'saved to their timeline' : "couldn't save that — check your connection");
  }, [advanceFrom, resolve]);

  const doDismiss = useCallback((item: SuggestionItem) => {
    advanceFrom(item.id);
    dismiss(item.id);
    setToast("dismissed — f8 won't suggest it again");
  }, [advanceFrom, dismiss]);

  const doUndo = useCallback(async (id: string, reason?: 'wrong_person') => {
    const ok = await undo(id, reason);
    if (!ok) { setToast("couldn't undo — it's still saved"); return; }
    // The two undos mean different things and the toast is the only place
    // that says which one landed: "wrong fact" leaves the alias alone,
    // "wrong person" tombstones it so f8 stops making that link.
    setToast(reason === 'wrong_person'
      ? 'not saved — f8 won’t link that name to them again'
      : 'not saved — f8 won’t suggest this again');
  }, [undo]);

  // What a blanket save can actually act on: has a target (or is new_person,
  // which the server auto-creates for) and isn't an identity chooser —
  // choosers and unassigned content items stay for their pickers.
  const bulkSavable = useCallback(
    (i: SuggestionItem) =>
      (Boolean(i.personId) || i.type === 'new_person') &&
      !(i.kind === 'identity' && i.candidates.length > 0),
    [],
  );

  const acceptAll = useCallback((list: SuggestionItem[]) => {
    const pending = list.filter((i) => !resolved[i.id]);
    const targets = pending.filter(bulkSavable);
    if (!targets.length) return;
    targets.forEach((i) => { void resolve(i.id, 'accept'); });
    setToast(targets.length < pending.length
      ? `saving ${targets.length} — the rest need a person picked first`
      : 'saving…');
  }, [resolved, resolve, bulkSavable]);

  /* ── keyboard ── */
  const onKey = useCallback((e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
    const list = visibleItems.filter((i) => !resolved[i.id]);
    const idx = list.findIndex((i) => i.id === focus);
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = list[idx + 1] || list[0];
      if (next) setFocus(next.id);
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = idx > 0 ? list[idx - 1] : list[list.length - 1];
      if (prev) setFocus(prev.id);
    } else if (e.key === 'a' && focus && !resolved[focus]) {
      const item = list.find((i) => i.id === focus);
      // `a` cannot answer "which Sarah?" — chooser items need a click. Same
      // for unassigned content items: no target means the picker has to be
      // used. new_person is fine — the server auto-creates on a bare accept.
      if (item && bulkSavable(item)) void doAccept(item);
    } else if (e.key === 'x' && focus && !resolved[focus]) {
      const item = list.find((i) => i.id === focus);
      if (item) doDismiss(item);
    }
  }, [visibleItems, resolved, focus, doAccept, doDismiss, bulkSavable]);

  // First focus once data lands.
  useEffect(() => {
    if (focus || !visibleItems.length) return;
    const first = visibleItems.find((i) => !resolved[i.id]);
    if (first) setFocus(first.id);
  }, [visibleItems, focus, resolved]);

  /* ── render ── */
  return (
    <div tabIndex={0} onKeyDown={onKey} style={{ display: 'flex', minHeight: '100%', outline: 'none', position: 'relative' }}>
      <SEO title="Insights" description="Review what f8 learned about your people." noindex />

      {/* queue */}
      <div style={{ flex: 1, minWidth: 0, padding: '26px 30px 60px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 6, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.03em', color: '#fff' }}>what f8 learned</h1>
              <div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>
                {!ready ? 'looking…'
                  : caughtUp ? 'all caught up · nothing left to review'
                    : `${pendingTotal} thing${pendingTotal === 1 ? '' : 's'} to review · waiting for you`}
              </div>
            </div>
            {pendingVisible.filter(bulkSavable).length > 1 ? (
              <Btn kind="quiet" onClick={() => acceptAll(pendingVisible)}>
                <Check size={12} />save all{filter !== 'all' ? ' shown' : ''}
              </Btn>
            ) : null}
          </div>

          {/* session progress — how far through THIS visit's queue you are */}
          {sessionTotal > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 20px' }}>
              <div style={{ flex: 1, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                <div style={{ width: `${(sessionDone / sessionTotal) * 100}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg,#f0abfc,#a855f7)', transition: 'width .25s' }} />
              </div>
              <span style={{ fontSize: 11.5, color: T.dim, fontVariantNumeric: 'tabular-nums' }}>{sessionDone} / {sessionTotal} reviewed</span>
            </div>
          ) : null}

          {/* person filter */}
          {groups.length > 1 ? (
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 20 }}>
              {[{ id: 'all', label: 'everyone', n: items.filter((i) => !resolved[i.id]).length },
                ...groups.map((g) => ({ id: g.key, label: g.name, n: g.pendingCount }))].map((f) => {
                const on = filter === f.id;
                return (
                  <button key={f.id} onClick={() => setFilter(f.id)} style={{
                    appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 999,
                    background: on ? 'rgba(217,70,239,0.16)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${on ? 'rgba(217,70,239,0.42)' : T.border}`,
                    color: on ? '#fff' : 'rgba(232,220,250,0.72)', fontSize: 12.5, fontWeight: 600,
                  }}>
                    {f.label}
                    <span style={{ fontSize: 11, fontWeight: 700, color: f.n ? (on ? T.magenta : T.dim) : 'rgba(220,200,240,0.28)', fontVariantNumeric: 'tabular-nums' }}>{f.n}</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {error ? (
            <div className="glass-panel" style={{ padding: 16, marginBottom: 18 }}>
              <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.55 }}>
                couldn&apos;t load what f8 noted — refresh to try again.
              </div>
            </div>
          ) : null}

          {caughtUp && !error ? (
            <div style={{
              padding: '18px 20px', borderRadius: 16, background: 'rgba(52,211,153,0.07)',
              border: '1px solid rgba(52,211,153,0.24)', marginBottom: 20,
              display: 'flex', alignItems: 'center', gap: 13,
            }}>
              <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(52,211,153,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Check size={15} color={T.green} strokeWidth={3} />
              </span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>that&apos;s everything</div>
                <div style={{ fontSize: 12.5, color: T.muted, marginTop: 3 }}>
                  f8 keeps listening while you chat. it saves the ones it&apos;s sure of and everything else lands here.
                </div>
              </div>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {visibleGroups.map((g) => {
              const gp = g.list.filter((i) => !resolved[i.id]);
              return (
                <div key={g.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 11 }}>
                    {g.key !== '__unmatched__' ? <MonoAvatar name={g.name} size={28} /> : null}
                    <span style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.015em', color: '#fff' }}>{g.name}</span>
                    <div style={{ flex: 1, height: 1, background: T.border }} />
                    {gp.filter(bulkSavable).length > 1 ? (
                      <button onClick={() => acceptAll(gp)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: T.magenta }}>
                        save all {gp.filter(bulkSavable).length}
                      </button>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {g.list.map((i) => {
                      const state = resolved[i.id];
                      return state ? (
                        <ReceiptRow
                          key={i.id}
                          item={i}
                          state={state}
                          personName={i.personName}
                          firstName={i.personId ? people[i.personId]?.firstName || null : null}
                          onUndo={() => { void doUndo(i.id); }}
                          onWrongPerson={() => { void doUndo(i.id, 'wrong_person'); }}
                          onUndoDismiss={() => { undoDismiss(i.id); }}
                        />
                      ) : (
                        <QueueItem
                          key={i.id}
                          item={i}
                          focused={focus === i.id}
                          people={people}
                          onFocus={() => setFocus(i.id)}
                          onAccept={(pid) => { void doAccept(i, undefined, pid); }}
                          onEdit={(edits, pid) => { void doAccept(i, edits, pid); }}
                          onDismiss={() => doDismiss(i)}
                          onChoose={(pid) => { void doAccept(i, undefined, pid); }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {sessionTotal > 0 ? (
            <div style={{ textAlign: 'center', fontSize: 11.5, color: T.dim, marginTop: 26 }}>
              {caughtUp ? 'f8 will keep listening while you chat.' : 'items you don’t decide on expire after 30 days.'}
            </div>
          ) : null}
        </div>
      </div>

      {/* sidebar */}
      {wide ? (
        <aside style={{
          width: 288, flexShrink: 0, borderLeft: `1px solid ${T.border}`,
          background: 'rgba(255,255,255,0.018)', padding: '26px 20px 30px',
          display: 'flex', flexDirection: 'column', gap: 18,
        }}>
          <div style={{ padding: '15px 16px', borderRadius: 16, background: 'rgba(217,70,239,0.06)', border: '1px solid rgba(217,70,239,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', background: 'radial-gradient(circle at 32% 30%, #f0abfc, #d946ef 52%, #7c3aed)', boxShadow: '0 0 12px rgba(217,70,239,0.6)' }} />
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.magenta }}>how this works</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.6 }}>
              when f8 is sure who something is about, it saves it and shows you what it did. anything it&apos;s unsure of waits here for you — and every save can be undone.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: T.dim, marginBottom: 10 }}>keyboard</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {[
                [<span key="jk" style={{ display: 'inline-flex', gap: 4 }}><Key>J</Key><Key>K</Key></span>, 'move through the queue'],
                [<Key key="a">A</Key>, 'save the focused item'],
                [<Key key="x">X</Key>, 'dismiss it'],
              ].map(([k, l], n) => (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ display: 'inline-flex', gap: 4, width: 52 }}>{k}</span>
                  <span style={{ fontSize: 12, color: T.muted }}>{l as string}</span>
                </div>
              ))}
            </div>
          </div>

          {ledger && ledger.total > 0 ? (
            <>
              <div style={{ height: 1, background: T.border }} />
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: T.dim, marginBottom: 10 }}>already saved</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', color: '#fff' }}>{ledger.total}</span>
                  <span style={{ fontSize: 12, color: T.muted }}>
                    thing{ledger.total === 1 ? '' : 's'} across {ledger.people} {ledger.people === 1 ? 'person' : 'ppl'}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {([['facts', 'fact', T.violet], ['events & dates', null, T.sky], ['commitments', 'commitment', T.amber], ['notes', 'note', T.green]] as const).map(([label, kind, colour]) => {
                    const n = kind === null
                      ? (ledger.byKind.event || 0) + (ledger.byKind.date || 0)
                      : (ledger.byKind[kind] || 0) + (kind === 'fact' ? (ledger.byKind.identity || 0) : 0);
                    if (!n) return null;
                    return (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: colour }} />
                        <span style={{ flex: 1, fontSize: 12.5, color: T.muted }}>{label}</span>
                        <span style={{ fontSize: 12, color: T.dim, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          <div style={{ height: 1, background: T.border }} />
          <div style={{ fontSize: 11.5, color: T.dim, lineHeight: 1.6 }}>
            undecided items expire after 30 days and are counted out of every badge.
          </div>
          <Btn kind="ghost" onClick={() => navigate('/contacts')} style={{ justifyContent: 'flex-start', padding: 0 }}>
            ← back to my ppl
          </Btn>
        </aside>
      ) : null}

      {/* toast */}
      {toast ? (
        <div style={{
          position: 'fixed', left: '50%', bottom: 26, transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 999,
          background: 'rgba(24,14,32,0.97)', border: '1px solid rgba(255,255,255,0.16)',
          boxShadow: '0 18px 44px rgba(0,0,0,0.55)', fontSize: 12.5, fontWeight: 600, color: '#fff', zIndex: 60,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.green }} />{toast}
        </div>
      ) : null}
    </div>
  );
}
